// src/registered/site-admin/knowledge_base/KnowledgeBaseDTO.js
//
// Knowledge Base — domain model.
//
// Cloned from precmed-engine's Knowledge Base structure
// (Services/KnowledgeBaseService.cs + kb.Document / kb.DocumentChunk) and the
// precsion-med-react compliance channel (channels/compliance ComplianceModel +
// KnowledgeBasePage). Re-shaped to the SUSTINA site-admin feature conventions
// used by report_component_library.
//
// A "knowledge document" is a procedure, policy or record the platform is
// allowed to draw on. On registration its text is split into passages
// ("chunks") that a later semantic index / assistant can retrieve and cite.

// Fallback pick-lists. The live values come from
// Ref.ESG_KnowledgeDocumentType / Ref.ESG_KnowledgeDocumentStatus via the
// service; these keep the screen usable if that lookup call fails.
export const DOCUMENT_TYPES = [
  { code: "SOP", label: "Standard operating procedure" },
  { code: "WORK_INSTRUCTION", label: "Work instruction" },
  { code: "POLICY", label: "Policy" },
  { code: "REGULATORY_GUIDANCE", label: "Regulatory guidance" },
  { code: "DISCLOSURE_RECORD", label: "Disclosure record" },
  { code: "XBRL_REPORT", label: "XBRL / iXBRL report" },
  { code: "EVIDENCE", label: "Evidence / supporting record" },
  { code: "OTHER", label: "Other" },
];

export const DOCUMENT_STATUSES = [
  { code: "PENDING", label: "Pending" },
  { code: "INDEXED", label: "Indexed" },
  { code: "STALE", label: "Stale" },
  { code: "SUPERSEDED", label: "Superseded" },
  { code: "FAILED", label: "Failed" },
];

export const SOURCE_KINDS = [
  { code: "text", label: "Paste text" },
  { code: "file", label: "Upload file" },
  { code: "url", label: "From URL" },
  // "share" — pick from an on-prem network share — is reserved for a later
  // phase (needs an agent behind the Cloudflare tunnel; see the feature memo).
];

// What the file picker accepts. The knowledge processor Lambda detects the real
// format from content, so these are hints for the OS dialog only.
export const FILE_ACCEPT = [
  ".pdf",
  ".docx",
  ".doc",
  ".md",
  ".markdown",
  ".txt",
  ".html",
  ".htm",
  ".xhtml",
  ".xml",
  ".xbrl",
].join(",");

export const MAX_SOURCE_BYTES = 40 * 1024 * 1024; // mirrors the Lambda ceiling

// Passage sizing — mirrors the intent of precmed-engine Lib/Chunker.cs: pack
// paragraphs up to a target size, hard-split anything longer on sentence
// boundaries. Approx token count = chars / 4 (same heuristic as the engine).
export const CHUNK_TARGET_CHARS = 1200;

const approxTokens = (s) => Math.max(1, Math.ceil((s || "").length / 4));

/**
 * Split raw document text into ordered passages.
 * @param {string} text
 * @returns {{ chunkIndex:number, bodyText:string, tokenCount:number, section:(string|null) }[]}
 */
export const chunkText = (text) => {
  const clean = String(text || "").replace(/\r\n/g, "\n").trim();
  if (!clean) return [];

  const paragraphs = clean
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);

  const out = [];
  const push = (body) => {
    const b = body.trim();
    if (b) out.push({ chunkIndex: 0, bodyText: b, tokenCount: approxTokens(b), section: null });
  };

  let buffer = "";
  const flush = () => {
    if (buffer) push(buffer);
    buffer = "";
  };

  for (const para of paragraphs) {
    if (para.length >= CHUNK_TARGET_CHARS) {
      flush();
      const sentences = para.split(/(?<=[.!?])\s+/);
      let s = "";
      for (const sentence of sentences) {
        const candidate = s ? `${s} ${sentence}` : sentence;
        if (candidate.length > CHUNK_TARGET_CHARS && s) {
          push(s);
          s = sentence;
        } else {
          s = candidate;
        }
      }
      if (s) push(s);
    } else if (buffer && `${buffer}\n\n${para}`.length > CHUNK_TARGET_CHARS) {
      flush();
      buffer = para;
    } else {
      buffer = buffer ? `${buffer}\n\n${para}` : para;
    }
  }
  flush();

  return out.map((c, i) => ({ ...c, chunkIndex: i }));
};

/** Best-effort display name for a URL ingest — last path segment, else host. */
export const deriveNameFromUrl = (url) => {
  try {
    const u = new URL(url);
    const seg = u.pathname.split("/").filter(Boolean).pop();
    if (seg) {
      const cleaned = decodeURIComponent(seg)
        .replace(/\.[a-z0-9]{2,5}$/i, "")
        .replace(/[-_]+/g, " ")
        .trim();
      if (cleaned) return cleaned.slice(0, 380);
    }
    return u.host;
  } catch {
    return "";
  }
};

let _seq = 0;
export const newLocalId = (prefix = "kd") => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  _seq += 1;
  return `${prefix}_${Date.now()}_${_seq}`;
};

export class KnowledgeDocumentChunkDTO {
  constructor({
    chunkId = null,
    chunkIndex = 0,
    bodyText = "",
    tokenCount = 0,
    section = null,
  } = {}) {
    this.chunkId = chunkId || newLocalId("chunk");
    this.chunkIndex = chunkIndex;
    this.bodyText = bodyText;
    this.tokenCount = tokenCount || approxTokens(bodyText);
    this.section = section || null;
  }
}

export default class KnowledgeDocumentDTO {
  constructor({
    knowledgeDocumentId = null,
    name = "",
    documentTypeCode = DOCUMENT_TYPES[0].code,
    documentTypeLabel = "",
    categoryTag = "",
    effectiveDate = "",
    statusCode = "PENDING",
    statusLabel = "",
    sourceKind = "text",
    sourceUrl = "",
    rawText = "",
    chunkCount = 0,
    chunks = [],
    version = 1,
    isActive = true,
    createdBySiteUserGuid = "",
    createdAtUtc = null,
    updatedAtUtc = null,
  } = {}) {
    this.knowledgeDocumentId = knowledgeDocumentId || newLocalId("kd");
    this.name = name;
    this.documentTypeCode = documentTypeCode;
    this.documentTypeLabel = documentTypeLabel;
    this.categoryTag = categoryTag;
    this.effectiveDate = effectiveDate ? String(effectiveDate).slice(0, 10) : "";
    this.statusCode = statusCode || "PENDING";
    this.statusLabel = statusLabel;
    this.sourceKind = SOURCE_KINDS.some((s) => s.code === sourceKind) ? sourceKind : "text";
    this.sourceUrl = sourceUrl || "";
    this.rawText = rawText || "";
    this.chunkCount = Number.isFinite(chunkCount) ? chunkCount : 0;
    this.chunks = (Array.isArray(chunks) ? chunks : [])
      .map((c) => new KnowledgeDocumentChunkDTO(c))
      .sort((a, b) => a.chunkIndex - b.chunkIndex);
    this.version = Number.isFinite(version) && version > 0 ? version : 1;
    this.isActive = isActive !== false;
    this.createdBySiteUserGuid = createdBySiteUserGuid;
    this.createdAtUtc = createdAtUtc;
    this.updatedAtUtc = updatedAtUtc;
  }

  /** Compact row for the list view. */
  toSummary() {
    return {
      knowledgeDocumentId: this.knowledgeDocumentId,
      name: this.name,
      documentTypeCode: this.documentTypeCode,
      documentTypeLabel: this.documentTypeLabel,
      categoryTag: this.categoryTag,
      effectiveDate: this.effectiveDate,
      statusCode: this.statusCode,
      statusLabel: this.statusLabel,
      sourceKind: this.sourceKind,
      chunkCount: this.chunkCount,
      version: this.version,
      isActive: this.isActive,
      createdAtUtc: this.createdAtUtc,
    };
  }
}
